<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, ExternalLink, ListChecks, Search, ShieldCheck } from "lucide-vue-next";
import type { BriefSolution } from "@/types";

const props = defineProps<{ solution: BriefSolution | null; confirming?: boolean }>();
const emit = defineEmits<{ confirm: [] }>();
const tasks = computed(() => props.solution?.tasks || []);
const solutionPanel = ref<HTMLElement | null>(null);
const mobilePage = ref(0);
const activeTaskIndex = computed(() => Math.min(Math.max(mobilePage.value - 1, 0), Math.max(tasks.value.length - 1, 0)));
const confirmPageIndex = computed(() => tasks.value.length + 1);
const mobilePageCount = computed(() => tasks.value.length + 2);
const mobileProgress = computed(() =>
  Math.round(((mobilePage.value + 1) / mobilePageCount.value) * 100),
);
const mobilePageLabel = computed(() => {
  if (mobilePage.value === 0) return "方案概览";
  if (mobilePage.value === confirmPageIndex.value) return "核对并确认";
  return `第 ${mobilePage.value} 项 · ${tasks.value[mobilePage.value - 1]?.titleClient || "执行任务"}`;
});
let touchStartX = 0;
let touchStartY = 0;

watch(
  () => [props.solution?.id, tasks.value.length],
  () => {
    mobilePage.value = 0;
  },
);

async function showMobilePage(index: number) {
  mobilePage.value = Math.min(Math.max(index, 0), mobilePageCount.value - 1);
  await nextTick();
  const scroller = solutionPanel.value?.closest(".task-panel") as HTMLElement | null;
  scroller?.scrollTo({ top: 0, behavior: "smooth" });
}

function handleTouchStart(event: TouchEvent) {
  touchStartX = event.changedTouches[0]?.clientX || 0;
  touchStartY = event.changedTouches[0]?.clientY || 0;
}

function handleTouchEnd(event: TouchEvent) {
  if (!window.matchMedia("(max-width: 700px)").matches) return;
  const endX = event.changedTouches[0]?.clientX || touchStartX;
  const endY = event.changedTouches[0]?.clientY || touchStartY;
  const distanceX = endX - touchStartX;
  const distanceY = endY - touchStartY;
  if (Math.abs(distanceX) < 52 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.25) return;
  void showMobilePage(mobilePage.value + (distanceX < 0 ? 1 : -1));
}
</script>

<template>
  <section ref="solutionPanel" class="solution-panel" @touchstart.passive="handleTouchStart" @touchend.passive="handleTouchEnd">
    <template v-if="solution">
      <section class="mobile-solution-status" aria-live="polite">
        <div><span>{{ mobilePageLabel }}</span><b>{{ mobilePage + 1 }} / {{ mobilePageCount }}</b></div>
        <div class="mobile-solution-progress"><i :style="{ width: `${mobileProgress}%` }"></i></div>
      </section>
      <section class="solution-overview-page" :class="{ 'mobile-active': mobilePage === 0 }">
        <header>
          <div>
            <span class="eyebrow">完整方案</span>
            <h2>{{ solution.clientContent.title || "等待 AI 整理方案" }}</h2>
          </div>
          <span class="readiness">完整度 {{ solution.readiness.score }}%</span>
        </header>
        <div class="solution-copy">{{ solution.clientContent.fullPlan || solution.clientContent.summary }}</div>
        <section v-if="solution.clientContent.researchSources?.length" class="research">
          <h3><Search :size="17" />联网参考</h3>
          <p v-if="solution.clientContent.researchSummary">{{ solution.clientContent.researchSummary }}</p>
          <a v-for="source in solution.clientContent.researchSources" :key="source.url" :href="source.url" target="_blank" rel="noopener noreferrer"><span><b>{{ source.title }}</b><small>{{ source.url }}</small></span><ExternalLink :size="14" /></a>
        </section>
      </section>
      <section class="solution-tasks" :class="{ 'mobile-active': mobilePage > 0 && mobilePage < confirmPageIndex }">
        <header class="task-pager-head">
          <h3><ListChecks :size="17" />两级执行清单</h3>
          <span v-if="tasks.length">{{ activeTaskIndex + 1 }} / {{ tasks.length }}</span>
        </header>
        <div class="task-pages">
          <article
            v-for="(task, index) in tasks"
            :key="task.stableKey"
            :class="{ active: index === activeTaskIndex }"
          >
            <span>{{ index + 1 }}</span>
            <div>
              <b>{{ task.titleClient }}</b>
              <div class="source-copy"><span>{{ task.sourceCopy ? "成品文字 · 保持原文" : "客户任务说明" }}</span><p>{{ task.sourceCopy || task.scopeClient }}</p></div>
              <div class="task-meta"><CalendarDays :size="14" />{{ task.plannedStartAt }} 至 {{ task.plannedEndAt }}</div>
              <div v-if="task.clientDeliverables.length" class="deliverables"><span>交付</span><b>{{ task.clientDeliverables.join(" · ") }}</b></div>
              <details class="execution-details">
                <summary><ListChecks :size="15" />团队执行细节 <small>{{ task.checklist.reduce((sum, group) => sum + group.children.length, 0) }} 项</small></summary>
                <ol class="groups">
                  <li v-for="group in task.checklist" :key="group.stableKey">
                    <div class="group-head"><b>{{ group.title }}</b><time>{{ group.plannedStartAt }} 至 {{ group.plannedEndAt }}</time></div>
                    <p>{{ group.description }}</p>
                    <ul>
                      <li v-for="child in group.children" :key="child.stableKey">
                        <div><b>{{ child.title }}</b><time>{{ child.plannedStartAt }} 至 {{ child.plannedEndAt }}</time></div>
                        <p>{{ child.description }}</p>
                        <small>核查：{{ child.acceptanceCriteria.join("；") }}</small>
                      </li>
                    </ul>
                  </li>
                </ol>
              </details>
            </div>
          </article>
        </div>
      </section>
      <section class="solution-confirm-page" :class="{ 'mobile-active': mobilePage === confirmPageIndex }">
        <header>
          <span class="eyebrow">最后一步</span>
          <h2>核对并确认方案</h2>
        </header>
        <section v-if="solution.requirements?.length" class="requirements">
          <h3><ShieldCheck :size="17" />提交前核查事项</h3>
          <article v-for="item in solution.requirements" :key="item.id">
            <b>{{ item.title }}</b><p>{{ item.description }}</p>
          </article>
        </section>
        <div v-if="solution.readiness.warnings.length" class="warnings">
          <b>仍可补充</b>
          <ul><li v-for="item in solution.readiness.warnings" :key="item">{{ item }}</li></ul>
        </div>
        <button
          class="btn btn--primary confirm"
          :disabled="!solution.readiness.readyForClientReview || confirming"
          @click="emit('confirm')"
        >
          <CheckCircle2 :size="17" />
          {{ confirming ? "正在创建项目草稿" : "确认方案并创建项目草稿" }}
        </button>
        <p v-if="!solution.readiness.readyForClientReview" class="muted gate">
          方案仍缺少：{{ solution.readiness.missingBlockingFields.join("、") }}
        </p>
      </section>
      <nav class="mobile-solution-nav" aria-label="切换方案页面">
        <button type="button" title="上一页" :disabled="mobilePage === 0" @click="showMobilePage(mobilePage - 1)"><ChevronLeft :size="20" /></button>
        <span>{{ mobilePage + 1 }} / {{ mobilePageCount }} · {{ mobilePageLabel }}</span>
        <button type="button" title="下一页" :disabled="mobilePage >= mobilePageCount - 1" @click="showMobilePage(mobilePage + 1)"><ChevronRight :size="20" /></button>
      </nav>
    </template>
    <template v-else>
      <header><div><span class="eyebrow">完整方案</span><h2>等待 AI 整理方案</h2></div></header>
      <p class="muted empty">发送需求后，AI 的完整方案会显示在这里。</p>
    </template>
  </section>
</template>

<style scoped>
.solution-panel,
.solution-overview-page,
.solution-confirm-page {
  display: grid;
  gap: 16px;
}
.solution-overview-page > header,
.solution-confirm-page > header,
.solution-panel > header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.solution-panel h2,
.solution-panel h3 {
  margin: 0;
}
.solution-panel h2 { font-size: 18px; }
.eyebrow { display: block; margin-bottom: 3px; color: var(--muted); font-size: 12px; }
.readiness { white-space: nowrap; padding: 4px 7px; background: #e8f5f1; color: var(--success); font-size: 12px; }
.solution-copy { white-space: pre-wrap; padding: 14px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); line-height: 1.7; }
.research { display: grid; gap: 6px; padding: 12px 0; border-bottom: 1px solid var(--line); }
.research h3,
.solution-tasks h3,
.requirements h3 { display: flex; align-items: center; gap: 7px; font-size: 15px; }
.research > p { margin: 0; color: var(--muted); }
.research a { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 8px; border: 1px solid var(--line); color: var(--ink); text-decoration: none; }
.research a span { min-width: 0; }
.research a b,
.research a small { display: block; }
.research a small { overflow: hidden; color: var(--muted); font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.solution-tasks { display: grid; gap: 8px; }
.task-pager-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.task-pager-head > span { display: none; color: var(--muted); font-size: 12px; }
.task-pages > article { display: grid; grid-template-columns: 26px minmax(0, 1fr); gap: 8px; padding: 12px 0; border-bottom: 1px solid var(--line); }
.solution-tasks article > span { display: grid; width: 24px; height: 24px; place-items: center; background: #f1f4f4; color: var(--muted); font-size: 12px; }
.solution-tasks p { margin: 3px 0; color: var(--muted); }
.source-copy { margin-top: 10px; padding: 12px; border-left: 3px solid var(--brand); background: #f6f8f8; }
.source-copy span,.deliverables span { display: block; margin-bottom: 5px; color: var(--brand-dark); font-size: 11px; font-weight: 700; }
.source-copy p { margin: 0; color: var(--ink); line-height: 1.65; white-space: pre-wrap; }
.deliverables { margin-top: 10px; }
.deliverables b { font-size: 13px; }
.execution-details { margin-top: 12px; border-top: 1px solid var(--line); }
.execution-details summary { display: flex; align-items: center; gap: 7px; padding: 11px 0; color: var(--muted); cursor: pointer; font-weight: 700; }
.execution-details summary small { margin-left: auto; font-weight: 400; }
.task-meta { display: flex; align-items: center; gap: 5px; margin-top: 7px; color: var(--muted); font-size: 12px; }
.groups { display: grid; gap: 9px; margin: 12px 0 0; padding: 0; list-style: none; }
.groups > li { padding: 10px; border-left: 3px solid #81949a; background: #f8fafa; }
.group-head,
.groups ul li > div { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.groups time { white-space: nowrap; color: var(--muted); font-size: 11px; }
.groups ul { display: grid; gap: 7px; margin: 9px 0 0; padding: 0 0 0 18px; list-style: none; }
.groups ul li { padding: 8px 0; border-top: 1px solid var(--line); }
.groups small { display: block; margin-top: 4px; color: var(--brand-dark); }
.requirements { display: grid; gap: 0; padding: 13px 0; border-top: 1px solid var(--line); }
.requirements article { padding: 9px 0; border-bottom: 1px solid var(--line); }
.requirements p { margin: 3px 0; color: var(--muted); }
.warnings { padding: 11px; border-left: 3px solid #d98b21; background: #fff8ed; }
.warnings ul { margin: 5px 0 0; padding-left: 18px; }
.confirm { display: flex; align-items: center; justify-content: center; gap: 7px; width: 100%; }
.gate,
.empty { margin: 0; text-align: center; }
.mobile-solution-status,
.mobile-solution-nav { display: none; }

@media (max-width: 700px) {
  .solution-panel {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr) auto;
    gap: 0;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background: #fff;
    touch-action: pan-y;
  }
  .mobile-solution-status {
    position: relative;
    z-index: 4;
    display: grid;
    gap: 8px;
    grid-row: 1;
    margin: 0;
    padding: 11px 14px 10px;
    border-bottom: 1px solid var(--line);
    background: #fff;
  }
  .mobile-solution-status>div:first-child{display:flex;align-items:center;justify-content:space-between;gap:12px}.mobile-solution-status span{min-width:0;overflow:hidden;font-size:13px;font-weight:700;text-overflow:ellipsis;white-space:nowrap}.mobile-solution-status b{flex:0 0 auto;color:var(--muted);font-size:11px}
  .mobile-solution-progress{height:3px;margin:0 -14px -10px;overflow:hidden;background:#e7ecec}.mobile-solution-progress i{display:block;height:100%;background:var(--brand);transition:width .2s ease}
  .solution-overview-page,
  .solution-tasks,
  .solution-confirm-page {
    display: none;
    grid-row: 2;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 16px 14px 24px;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
  .solution-overview-page.mobile-active,
  .solution-tasks.mobile-active,
  .solution-confirm-page.mobile-active { display: grid; }
  .task-pager-head > span { display: block; }
  .task-pages > article { display: none; grid-template-columns: 32px minmax(0,1fr);gap:10px;padding:8px 0 14px;border-bottom:0; }
  .task-pages > article.active { display: grid; }
  .solution-tasks article > span { width: 30px; height: 30px; background: var(--brand-dark); color:#fff; }
  .source-copy{padding:12px;border-left:0;border-top:3px solid var(--brand)}.source-copy p{font-size:15px;line-height:1.6}.deliverables{padding:10px 0}.execution-details summary{min-height:46px;font-size:13px}
  .mobile-solution-nav {
    position: relative;
    z-index: 4;
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) 44px;
    align-items: center;
    gap: 8px;
    grid-row: 3;
    margin: 0;
    padding: 10px 14px calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--line);
    background: #fff;
    box-shadow: 0 -6px 18px rgba(25,45,55,.06);
  }
  .mobile-solution-nav button { display: grid; width: 44px; height: 44px; place-items: center; border: 1px solid var(--line); border-radius:6px; background:#f8fafa; color:var(--ink); }
  .mobile-solution-nav button:disabled { opacity: .32; }
  .mobile-solution-nav span { min-width: 0; overflow: hidden; color:var(--ink); font-size:12px; font-weight:700; text-align:center; text-overflow:ellipsis; white-space:nowrap; }
  .group-head,
  .groups ul li > div { display: grid; }
  .groups time { white-space: normal; }
  .groups ul { padding-left: 10px; }
  .groups > li { padding: 10px 8px; }
  .solution-overview-page>header,.solution-confirm-page>header{display:grid}.readiness{width:max-content}.solution-copy{font-size:14px;line-height:1.65}
}
</style>
